import { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { MessageCircle, Send, Loader2, CheckCheck, Tag, X, Check } from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';

/**
 * ProducerInquiriesTab — list + chat for "inquiry" ticket consultations.
 * Mounted inside EventManagePage under the "Consultas" tab.
 */
export default function ProducerInquiriesTab({ eventId }) {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedId, setSelectedId] = useState(null);
  const [thread, setThread] = useState({ inquiry: null, messages: [] });
  const [loadingThread, setLoadingThread] = useState(false);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  const loadList = async () => {
    setLoading(true);
    try {
      const params = { event_id: eventId };
      if (filter !== 'all') params.status = filter;
      const res = await api.get('/producers/me/inquiries', { params });
      setInquiries(res.data || []);
    } catch (err) {
      toast.error('No se pudo cargar las consultas');
    } finally {
      setLoading(false);
    }
  };

  const loadThread = async (id) => {
    setLoadingThread(true);
    try {
      const res = await api.get(`/producers/me/inquiries/${id}`);
      setThread(res.data);
      // refresh list to update unread counters
      loadList();
    } catch (err) {
      toast.error('No se pudo cargar la consulta');
    } finally {
      setLoadingThread(false);
    }
  };

  useEffect(() => { loadList(); /* eslint-disable-next-line */ }, [eventId, filter]);
  useEffect(() => { if (selectedId) loadThread(selectedId); /* eslint-disable-next-line */ }, [selectedId]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [thread.messages]);

  const sendReply = async (e) => {
    e.preventDefault();
    const body = reply.trim();
    if (!body || !selectedId) return;
    setSending(true);
    try {
      const res = await api.post(`/producers/me/inquiries/${selectedId}/messages`, { message: body });
      setThread((prev) => ({ ...prev, messages: [...prev.messages, res.data.message] }));
      setReply('');
      loadList();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo enviar');
    } finally {
      setSending(false);
    }
  };

  const setStatus = async (status) => {
    if (!selectedId) return;
    try {
      await api.put(`/producers/me/inquiries/${selectedId}/status`, { status });
      // If we're marking the tier as sold and the inquiry has a tier_id, suggest setting is_sold_out
      if (status === 'sold' && thread.inquiry?.tier_id) {
        await api.put(`/producers/me/events/${eventId}/tiers/${thread.inquiry.tier_id}/sold-out?is_sold_out=true`);
        toast.success('Marcado como vendido y tanda agotada en el listado público');
      } else {
        toast.success('Estado actualizado');
      }
      setThread((prev) => ({ ...prev, inquiry: { ...prev.inquiry, status } }));
      loadList();
    } catch (err) {
      toast.error('No se pudo actualizar el estado');
    }
  };

  const STATUS_OPTIONS = [
    { id: 'all', label: 'Todas' },
    { id: 'open', label: 'Pendientes' },
    { id: 'answered', label: 'Respondidas' },
    { id: 'sold', label: 'Vendidas' },
    { id: 'closed', label: 'Cerradas' },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 h-[70vh]">
      {/* List */}
      <div className="md:col-span-1 bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden flex flex-col">
        <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
          <h3 className="text-sm font-bold text-white flex items-center gap-2"><MessageCircle size={14} /> Consultas</h3>
          <span className="text-[10px] text-zinc-500">{inquiries.length}</span>
        </div>
        <div className="px-3 py-2 border-b border-zinc-800 overflow-x-auto whitespace-nowrap scrollbar-thin">
          {STATUS_OPTIONS.map((opt) => (
            <button
              key={opt.id}
              onClick={() => setFilter(opt.id)}
              className={`text-[11px] px-2.5 py-1 rounded-full mr-1.5 transition-colors ${filter === opt.id ? 'bg-violet-500 text-white' : 'bg-white/5 text-white/60 hover:bg-white/10'}`}
              data-testid={`inq-filter-${opt.id}`}
            >{opt.label}</button>
          ))}
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-6 text-center text-zinc-500"><Loader2 className="animate-spin mx-auto" size={18} /></div>
          ) : inquiries.length === 0 ? (
            <div className="p-6 text-center text-zinc-500 text-xs">No hay consultas en este filtro.</div>
          ) : (
            inquiries.map((q) => (
              <button
                key={q.id}
                onClick={() => setSelectedId(q.id)}
                className={`w-full text-left px-3 py-3 border-b border-zinc-800 hover:bg-white/5 transition-colors ${selectedId === q.id ? 'bg-violet-500/10' : ''}`}
                data-testid={`inq-item-${q.id}`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className={`w-1.5 h-1.5 rounded-full ${q.status === 'open' ? 'bg-amber-400' : q.status === 'answered' ? 'bg-violet-400' : q.status === 'sold' ? 'bg-emerald-400' : 'bg-zinc-500'}`} />
                  <p className="text-sm font-semibold text-white truncate flex-1">{q.buyer_name}</p>
                  {q.unread_count_producer > 0 && <span className="text-[10px] bg-pink-500 text-white px-1.5 py-0.5 rounded-full font-bold">{q.unread_count_producer}</span>}
                </div>
                <p className="text-[11px] text-zinc-400 truncate">{q.tier_name}</p>
                <p className="text-[10px] text-zinc-500 mt-0.5">{new Date(q.last_message_at).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</p>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Thread */}
      <div className="md:col-span-2 bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden flex flex-col">
        {!selectedId ? (
          <div className="flex-1 flex items-center justify-center text-zinc-500 text-sm">
            Seleccioná una consulta para responder
          </div>
        ) : loadingThread ? (
          <div className="flex-1 flex items-center justify-center"><Loader2 className="animate-spin text-zinc-500" /></div>
        ) : thread.inquiry ? (
          <>
            <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-bold text-white truncate">{thread.inquiry.buyer_name}</p>
                <p className="text-[11px] text-zinc-500 truncate">
                  {thread.inquiry.buyer_email} {thread.inquiry.buyer_phone && `· ${thread.inquiry.buyer_phone}`}
                </p>
                <p className="text-[10px] text-zinc-400 mt-0.5"><Tag size={9} className="inline mr-0.5" /> {thread.inquiry.tier_name}</p>
              </div>
              <div className="flex flex-wrap gap-1">
                {thread.inquiry.status !== 'sold' && (
                  <button onClick={() => setStatus('sold')} title="Marcar como vendida + agotar tanda" data-testid="inq-mark-sold"
                    className="text-[10px] bg-emerald-500 hover:bg-emerald-600 text-white px-2 py-1 rounded-md font-semibold flex items-center gap-1">
                    <Check size={11} /> Vendida
                  </button>
                )}
                {thread.inquiry.status !== 'closed' && (
                  <button onClick={() => setStatus('closed')} title="Cerrar conversación"
                    className="text-[10px] bg-white/10 hover:bg-white/15 text-white px-2 py-1 rounded-md flex items-center gap-1">
                    <X size={11} /> Cerrar
                  </button>
                )}
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-2">
              {thread.messages.map((m) => {
                const fromProducer = m.sender_type === 'producer';
                return (
                  <div key={m.id} className={`flex ${fromProducer ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[78%] rounded-2xl px-4 py-2.5 ${fromProducer ? 'bg-violet-500 text-white' : 'bg-white/5 border border-white/10 text-white'}`}>
                      <p className="text-[10px] uppercase tracking-wider opacity-70 mb-0.5">{m.sender_name}</p>
                      <p className="text-sm whitespace-pre-wrap leading-relaxed">{m.body}</p>
                      <p className="text-[10px] opacity-50 mt-1 flex items-center gap-1 justify-end">
                        {new Date(m.created_at).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                        {fromProducer && m.read_at && <CheckCheck size={11} />}
                      </p>
                    </div>
                  </div>
                );
              })}
              <div ref={endRef} />
            </div>
            {thread.inquiry.status !== 'closed' ? (
              <form onSubmit={sendReply} className="border-t border-zinc-800 px-4 py-3 flex items-end gap-2 bg-zinc-900">
                <textarea
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  rows={1}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(e); } }}
                  placeholder="Responder…"
                  className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm text-white placeholder-white/30 focus:border-white/30 focus:outline-none resize-none max-h-32"
                  data-testid="producer-reply-input"
                />
                <button
                  type="submit"
                  disabled={sending || !reply.trim()}
                  className="p-2 rounded-full bg-violet-500 hover:bg-violet-600 text-white disabled:opacity-40"
                  data-testid="producer-reply-send"
                ><Send size={14} /></button>
              </form>
            ) : (
              <div className="border-t border-zinc-800 px-4 py-3 text-center text-[11px] text-zinc-500">Conversación cerrada</div>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
}
